import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService'
import { VoidGearList, VoidQuestions, VoidGearListSummaryItem, VoidSizeQuantity } from './models/JobScaffoldInterfaces';
import JobScheduleVoidQuestionsData from './JobScheduleVoidQuestions';

interface JobScheduleVoidProps {
    jobQuoteItemId: number;     //use job quote item id as I want to get all scheduled void items
    showModal: boolean;
    hideModal(): void;
    saveModal(): void;
}

interface VoidTruckOption {
    id: number;             //schedule id
    name: string;           //schedule date and truck name
    typeId: number;         //Install / Dismantle
    quantity: number;       //number of voids allocated to this truck
}

const JobScheduleVoidModalData = (props: JobScheduleVoidProps) => {
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [loading, setLoading] = React.useState(false);
    const [loadingGearlist, setLoadingGearlist] = React.useState(false);
    const [truckOptions, setTruckOptions] = React.useState<VoidTruckOption[]>([]);
    const [voidNumberOptions, setVoidNumberOptions] = React.useState<number[]>([]);
    const [scheduleId, setScheduleId] = React.useState(-1);
    const [voidNumber, setVoidNumber] = React.useState(-1);
    const [voidDescription, setVoidDescription] = React.useState("");
    //selected truck with gearlist
    const [voidGearList, setVoidGearList] = React.useState<VoidGearList>({
        scheduleId: 0,
        unitNumber: "",
        voidNumber: 0,
        voidDescription: "",
        gearList: {
            braces: [],
            floorEnds: [],
            plateEnds: [],
            hookEnds: [],
            ladders: [],
            noLadder: false,
            noLadderReason: "",
            ladderExtras: [],
            accessPanels: [],
            noAccessPanels: false,
            noAccessPanelsReason: "",
            decks700: [],
            decks550: [],
            decks300: [],
            decks150: [],
            noDecks: false,
            noDecksReason: ""
        },
        isDirty: false
    });
    const [gearListSummary, setGearListSummary] = React.useState<VoidGearListSummaryItem[]>([]);

    React.useEffect(() => {
        if (props.jobQuoteItemId > 0) {
            setLoading(true);
            getData();
        }
    }, [props.jobQuoteItemId]);

    const getData = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/Job/GetScheduleVoidGearListTruckSummary?JobQuoteItemId=' + props.jobQuoteItemId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setTruckOptions(res.data.truckOptions);
            setGearListSummary(res.data.gearListSummary);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    React.useEffect(() => {
        if (voidNumber > 0) {
            //setLoadingGearlist(true);
            getVoidGearlist();
        } else {
            //setLoadingGearlist(false);
            resetGearlist();
        }
    }, [voidNumber]);


    const hideModal = () => {
        setErrors({});
        setLoading(false);
        setLoadingGearlist(false);
        setTruckOptions([]);
        setScheduleId(-1);
        setVoidNumber(-1);
        setVoidDescription("");
        resetGearlist();
        props.hideModal();
    }

    const resetGearlist = () => {
        var blankGearlist: VoidGearList = {
            scheduleId: 0,
            unitNumber: "",
            voidNumber: 0,
            voidDescription: "",
            gearList: {
                braces: [],
                floorEnds: [],
                plateEnds: [],
                hookEnds: [],
                ladders: [],
                noLadder: false,
                noLadderReason: "",
                ladderExtras: [],
                accessPanels: [],
                noAccessPanels: false,
                noAccessPanelsReason: "",
                decks700: [],
                decks550: [],
                decks300: [],
                decks150: [],
                noDecks: false,
                noDecksReason: ""
            },
            isDirty: false
        };
        setVoidGearList(blankGearlist);
    }

    const handleTruckChange = (e: any) => {
        e.preventDefault();
        var newScheduleId = e.target.value * 1;

        if (voidGearList.isDirty) {
            //save the existing list before changing
            saveGearList(newScheduleId, -1);
        } else {
            changeTrucks(newScheduleId);
        }
    } 

    const changeTrucks = (newScheduleId: number) => {
        setScheduleId(newScheduleId);
        setVoidNumber(-1);
        resetGearlist();

        if (newScheduleId < 1) {
            setScheduleId(-1);
            setVoidNumberOptions([]);
        } else {
            var truckItem = truckOptions.find(t => t.id === newScheduleId);
            if (truckItem) {
                //use qty to add void numbers
                var voidNumbers: number[] = [];
                for (var i = 0; i < truckItem.quantity; i++) {
                    voidNumbers.push(i + 1);
                }
                setVoidNumberOptions(voidNumbers);
            }
        }
    }

    const handleVoidNumberChange = (e: any) => {
        e.preventDefault();
        var newVoidNumber = e.target.value * 1;

        if (voidGearList.isDirty) {
            //save the existing list before changing
            saveGearList(-1, newVoidNumber);
        } else {
            setVoidNumber(newVoidNumber);
        }
    } 

    const getVoidGearlist = async () => {
        if (scheduleId > 0 && voidNumber > 0) {
            const token = await authService.getAccessToken();

            axios.get('api/Job/GetScheduleVoidGearList?ScheduleId=' + scheduleId + '&VoidNumber=' + voidNumber, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                setVoidGearList(res.data);
                setVoidDescription(res.data.description);
                setLoading(false);
                setLoadingGearlist(false);
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
    }

    const handleDescriptionChange = (e: any) => {
        e.preventDefault();

        setVoidDescription(prevState => (e.target.value));
        setVoidGearList(prevState => ({ ...prevState, isDirty: true }));
    }

    const updateVoidCheckbox = (fieldName: string, checked: boolean) => {
        var updatedGearList = { ...voidGearList.gearList };
        let change: any = {};
        change = updatedGearList;
        change[fieldName] = checked;
        setVoidGearList(prevState => ({ ...prevState, gearList: change, isDirty: true }));
    }

    const updateVoidText = (fieldName: string, fieldValue: string) => {
        var updatedGearList = { ...voidGearList.gearList };
        let change: any = {};
        change = updatedGearList;
        change[fieldName] = fieldValue;
        setVoidGearList(prevState => ({ ...prevState, gearList: change, isDirty: true }));
    }

    const updateVoidList = (listName: string, size: string, fieldValue: number) => {
        var updatedGearList = { ...voidGearList.gearList };
        let change: any = {};
        change = updatedGearList;
        var listToUpdate: VoidSizeQuantity[] = change[listName];
        var itemToUpdate = listToUpdate.find(l => l.size === size);
        if (itemToUpdate) {
            itemToUpdate.quantity = fieldValue;
        }
        setVoidGearList(prevState => ({ ...prevState, gearlist: change, isDirty: true }));
    }

    const save = () => {
        if (voidGearList.isDirty) {
            saveGearList( -1, -1);
        } else {
            toast.info("No items have changed.");
            hideModal();
        }
    }

    const saveGearList = async (newScheduleId: number, newVoidNumber: number) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var gearList = {
            scheduleId: scheduleId,
            voidNumber: voidNumber,
            description: voidDescription,
            gearlist: voidGearList.gearList
        };

        var gearListItems = {
            voidGearList: gearList,
            subId: user.sub
        };

        axios.post('api/Job/SaveVoidGearList', gearListItems, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Void Gear List successfully saved.");
                if (newScheduleId > 0) {
                    changeTrucks(newScheduleId);
                } else if (newVoidNumber > 0) {
                    setVoidNumber(newVoidNumber);
                }
                getData();
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const voidGearlistData = <JobScheduleVoidQuestionsData voidQuestions={voidGearList.gearList} fromCompleteJob={true} errors={errors} updateVoidCheckbox={updateVoidCheckbox} updateVoidText={updateVoidText} updateVoidList={updateVoidList} />

    const renderDetails = (
        <div className="static-modal">
            <Modal size="xl" show={props.showModal} onHide={hideModal}>
                <Modal.Header className="modal__header--colour">
                    <Modal.Title>Void Gear List</Modal.Title>
                    <button type="button" className="close" onClick={hideModal}>&times;</button>
                </Modal.Header>
                <Modal.Body>
                    <label className="input-group" htmlFor="truckOption">
                        <span className="label-small">Date / Truck</span>
                        <select className="select" id="scheduleId" name="scheduleId" value={scheduleId} onChange={(e) => handleTruckChange(e)}>
                            <option hidden defaultValue="-1"></option>
                            {truckOptions.map(truck =>
                                <option key={truck.id} value={truck.id}>{truck.name}</option>
                            )};
                        </select>
                    </label>

                    <label className="input-group marginTop10" htmlFor="truckOption">
                        <span className="label-small">Void Number</span>
                        <select className="select" id="voidNumber" name="voidNumber" value={voidNumber} onChange={(e) => handleVoidNumberChange(e)}>
                              <option hidden defaultValue="-1" value={-1}></option>
                            {voidNumberOptions.map(number =>
                                <option key={number} value={number}>{number}</option>
                            )};
                        </select>
                    </label>

                    <label className={voidNumber < 1 ? "hidden" : "input-group marginTop10"} htmlFor="voidDescription">
                        <span className="label-small">Description</span>
                        <input type='text' maxLength={50} id="voidDescription" name="voidDescription" className='' value={voidDescription} onChange={(e) => handleDescriptionChange(e)}></input>
                    </label>

                    <div className={loadingGearlist ? "" : "hidden"}>Loading...</div>

                    <div className="input-group-parent">
                        <div className={scheduleId > 0 && voidNumber > 0 && !loadingGearlist ? "" : "hidden"}>
                            {voidGearlistData}
                        </div>
                    </div>
                    <div className="input-group-parent">
                        <h2 className="mobile-margin marginLeft20">Summary</h2>
                        <div className="marginLeft20">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Size</th>
                                        <th>Install Qty</th>
                                        <th>Dismantle Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gearListSummary.map((item, index) =>
                                        <tr key={index}>
                                            <td className="table__text--align">{item.item}</td>
                                            <td className="table__text--align">{item.size}</td>
                                            <td className="table__text--align">{item.installQuantity}</td>
                                            <td className="table__text--align">{item.dismantleQuantity}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    
                </Modal.Body>
                <Modal.Footer>
                    <button className="popup-button--yes" type="button" onClick={save} disabled={truckOptions.length === 0}>Save</button>
                </Modal.Footer>
            </Modal>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobScheduleVoidModalData;